import React, { useEffect, useState, useCallback } from "react";
import { Clock, Loader2, Search, RefreshCw } from "lucide-react";
import cronstrue from "cronstrue";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { Pagination } from "./Pagination";

interface CronJobsProps {

    asset_id: string;
    client_id: string;
}

const getReadableSchedule = (job: any) => {
    // If all fields are hyphens or empty, it's not a standard cron schedule (often a systemd timer)
    if (
        (job.minute === '-' || !job.minute) &&
        (job.hour === '-' || !job.hour) &&
        (job.day_of_month === '-' || !job.day_of_month) &&
        (job.month === '-' || !job.month) &&
        (job.day_of_week === '-' || !job.day_of_week)
    ) {
        return "-";
    }

    const min = job.minute === '-' ? '*' : (job.minute || '*');
    const hr = job.hour === '-' ? '*' : (job.hour || '*');
    const dom = job.day_of_month === '-' ? '*' : (job.day_of_month || '*');
    const mon = job.month === '-' ? '*' : (job.month || '*');
    const dow = job.day_of_week === '-' ? '*' : (job.day_of_week || '*');
    const cronStr = `${min} ${hr} ${dom} ${mon} ${dow}`;

    try {
        if (cronStr === "* * * * *") return "Every minute";
        return cronstrue.toString(cronStr);
    } catch (e) {
        return cronStr; // Fallback if parsing fails
    }
};

const ScheduleDescription = ({ description }: { description: string }) => {
    if (!description) return <span className="text-slate-400 dark:text-gray-500">-</span>;
    
    // Split the description by ' | '
    const parts = description.split(' | ');
    let mainDesc = description;
    let state = null;
    let nextStr = null;
    let lastStr = null;
    
    if (parts.length > 1) {
        mainDesc = parts[0];
        
        parts.slice(1).forEach(part => {
            const trimmed = part.trim();
            if (trimmed.toLowerCase().startsWith('state:')) {
                state = trimmed.substring(6).trim().toLowerCase();
            } else if (trimmed.toLowerCase().startsWith('next:')) {
                nextStr = trimmed;
            } else if (trimmed.toLowerCase().startsWith('last:')) {
                lastStr = trimmed;
            }
        });
    }

    return (
        <div className="flex flex-col gap-2 items-start">
            <div className="line-clamp-2 hover:line-clamp-none transition-all text-slate-700 dark:text-gray-300 font-medium" title={mainDesc}>
                {mainDesc}
            </div>
            
            {(state || nextStr || lastStr) && (
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                    {state && (
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-widest border ${state === 'enabled' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20' : 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/20'}`}>
                            {state}
                        </span>
                    )}
                    {nextStr && (
                        <span className="px-1.5 py-0.5 rounded text-[8px] font-semibold bg-slate-100 dark:bg-gray-800/80 text-blue-700 dark:text-blue-400 border border-slate-200 dark:border-gray-700">
                            {nextStr}
                        </span>
                    )}
                    {lastStr && (
                        <span className="px-1.5 py-0.5 rounded text-[8px] font-semibold bg-slate-100 dark:bg-gray-800/80 text-amber-700 dark:text-orange-400 border border-slate-200 dark:border-gray-700">
                            {lastStr}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

const CronJobs: React.FC<CronJobsProps> = ({
    asset_id,
    client_id
}) => {
    const [cronData, setCronData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    
    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchCronJobs = useCallback(async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;
            const queryObj: any = { asset_id , client_id};
            if (debouncedSearchTerm) {
                queryObj.cron_file = { "$ilike": `%${debouncedSearchTerm}%` };
            }
            const params = {
                limit,
                skip,
                query: JSON.stringify(queryObj),
            };
            const response = await baseapi.get("/asset_cron_jobs", { params });
            
            if (response.data.status) {
                setCronData(response.data.data ?? []);
                setTotalRecords(response.data.total ?? 0);
            } else {
                setCronData([]);
                setTotalRecords(0);
            }
        } catch (err: any) {
            showError(err.message);
            setCronData([]);
            setTotalRecords(0);
        }
        setLoading(false);
    }, [asset_id, currentPage, limit, debouncedSearchTerm, client_id]);

    useEffect(() => {
        fetchCronJobs();
    }, [fetchCronJobs]);

    // Calculate total pages for the Pagination component
    const totalPages = Math.max(1, Math.ceil(totalRecords / limit));

    return (
        <div className="p-3 h-full flex flex-col text-slate-900 dark:text-gray-200">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-3 border-b border-slate-200 dark:border-gray-700 pb-2 shrink-0">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <Clock className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">System Cron Jobs</h2>
                        <p className="text-slate-500 dark:text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {totalRecords || '...'} found
                        </p>
                    </div>
                </div>

                {/* Search and Refresh */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchCronJobs}
                        disabled={loading}
                        className="p-2 bg-white dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                    <div className="relative w-64">
                        <Search className="w-3.5 h-3.5 text-slate-400 dark:text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search cron jobs..."
                            className="w-64 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-8 shadow-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-hidden flex flex-col bg-white dark:bg-gray-900/40 border border-slate-200 dark:border-gray-700/50 rounded-md shadow-sm">
                <div className="overflow-x-auto custom-scroll flex-1">
                    <table className="w-full text-left text-xs text-slate-800 dark:text-gray-100 border-collapse min-w-[600px]">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-slate-200 dark:border-gray-700/50">
                                <th className="px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        Cron File
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                       Schedule
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        User
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        Type
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        Description
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap min-w-[200px]">
                                    <div className="flex items-center gap-1.5">
                                     Command
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700/60 bg-white/50 dark:bg-gray-900/20 text-xs">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500 dark:text-gray-500">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-orange-500" />
                                        Loading cron jobs...
                                    </td>
                                </tr>
                            ) : cronData.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500 dark:text-gray-500 opacity-60">
                                        <Clock className="w-6 h-6 text-slate-400 dark:text-gray-700 mx-auto mb-2" />
                                        No Cron Jobs Found
                                    </td>
                                </tr>
                            ) : (
                                cronData.map((job) => (
                                    <tr key={job.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors group align-top">
                                        <td className="px-3 py-2 whitespace-nowrap text-[11px] font-medium text-slate-900 dark:text-gray-200">
                                            {job.cron_file || "-"}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <div className="font-semibold text-amber-700 dark:text-yellow-400 text-[10px]">
                                                {getReadableSchedule(job)}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-[10px] text-slate-600 dark:text-gray-300">
                                            {job.user || "-"}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-[9px] font-bold text-purple-700 dark:text-purple-400 uppercase tracking-wider">
                                                {job.cron_type || "-"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-400 min-w-[250px] max-w-[350px]">
                                            <ScheduleDescription description={job.schedule_description} />
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="px-2 py-1.5 bg-slate-100 dark:bg-black/40 border border-slate-200 dark:border-gray-700/50 rounded font-mono text-[10px] text-emerald-800 dark:text-green-400/90 break-all leading-relaxed max-h-[80px] overflow-y-auto custom-scrollbar group-hover:border-slate-300 dark:group-hover:border-gray-600 transition-colors">
                                                {job.command || "-"}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Integration of the Pagination Component */}
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    itemsPerPage={limit}
                    totalItems={totalRecords}
                    onItemsPerPageChange={(n) => {
                        setLimit(n);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
};

export default CronJobs;